import { ConsiderationField, PutCallRight } from '../../models/FlowOfFunds/flow-of-funds.model';
import { TypeOfConsideration } from '../../slices/marketplace/pricing/models/type-of-consideration.enum';
import { ConsiderationTreatment } from '../../slices/marketplace/pricing/models/consideration-treatment.enum';
import { TimelineParty } from '../../slices/marketplace/timeline/models/enums/timeline-party.enum';
import {
    considerationTreatmentName,
    considerationTypeName,
    formatMoney,
    formatMultiple,
    formatPercent,
    PUT_CALL_LABELS,
} from '../flowOfFundsFormat';

export type ConsiderationFieldKind =
    | 'currency'
    | 'percent'
    | 'multiple'
    | 'ratio'
    | 'days'
    | 'months'
    | 'integer'
    | 'boolean'
    | 'putCallRight'
    | 'considerationType'
    | 'considerationTreatment'
    | 'text';

const fieldKindMap: Record<ConsiderationField, ConsiderationFieldKind> = {
    [ConsiderationField.FaceValue]: 'currency',
    [ConsiderationField.FairValue]: 'currency',
    [ConsiderationField.EmployeePortion]: 'currency',
    [ConsiderationField.BuyerEmployeePortion]: 'currency',
    [ConsiderationField.ProjectedCashFlow]: 'currency',
    [ConsiderationField.FeePaymentTiming]: 'considerationTreatment',
    [ConsiderationField.IsConsiderationActive]: 'boolean',
    [ConsiderationField.ValueAtClosing]: 'currency',
    [ConsiderationField.Amount]: 'currency',
    [ConsiderationField.PaymentTiming]: 'text',
    [ConsiderationField.AnnualInterestAccrualRate]: 'percent',
    [ConsiderationField.IsContingent]: 'boolean',
    [ConsiderationField.ContingencyDependentVariable]: 'text',
    [ConsiderationField.ContingencyMinimumValue]: 'currency',
    [ConsiderationField.HasEmploymentContingency]: 'boolean',
    [ConsiderationField.EmploymentTailPeriodMonths]: 'months',
    [ConsiderationField.BuyerCalculationPeriodDays]: 'days',
    [ConsiderationField.SellerResponsePeriodDays]: 'days',
    [ConsiderationField.PaymentDateOffsetDays]: 'days',
    [ConsiderationField.PutCallRight]: 'putCallRight',
    [ConsiderationField.PutCallExplanation]: 'text',
    [ConsiderationField.DeferredVariableType]: 'considerationType',
    [ConsiderationField.CalculationDependentVariable]: 'text',
    [ConsiderationField.CalcDvBaselineValue]: 'currency',
    [ConsiderationField.CalculationMultiple]: 'multiple',
    [ConsiderationField.MaximumPayment]: 'currency',
    [ConsiderationField.PenaltyMultiple]: 'multiple',
    [ConsiderationField.DistributionPeriodicity]: 'text',
    [ConsiderationField.ExpectedTimingToRecap]: 'text',
    [ConsiderationField.ExpectedPayoutMultiple]: 'multiple',
    [ConsiderationField.AnnualDividendRate]: 'percent',
    [ConsiderationField.DividendPaymentTiming]: 'text',
    [ConsiderationField.CompoundingFrequency]: 'text',
    [ConsiderationField.IsConvertibleIntoCommon]: 'boolean',
    [ConsiderationField.ConversionRatio]: 'ratio',
    [ConsiderationField.Label]: 'text',
};

export const getFieldKind = (field: ConsiderationField): ConsiderationFieldKind =>
    fieldKindMap[field] ?? 'text';

const toPlainDecimalString = (num: number): string => {
    if (Number.isInteger(num)) {
        return num.toString();
    }
    const fixed = num.toFixed(15);
    return fixed.replace(/0+$/, '').replace(/\.$/, '');
};

export const toWireValue = (
    field: ConsiderationField,
    input: string | number | boolean | null
): string | null | undefined => {
    if (input === null || input === undefined || input === '') {
        return null;
    }

    const kind = getFieldKind(field);

    switch (kind) {
        case 'currency':
        case 'percent':
        case 'multiple':
        case 'ratio': {
            if (typeof input === 'number') {
                return toPlainDecimalString(input);
            }
            if (typeof input === 'string') {
                const trimmed = input.trim();
                if (trimmed === '') {
                    return null;
                }
                const num = Number(trimmed);
                return isNaN(num) ? undefined : toPlainDecimalString(num);
            }
            return undefined;
        }

        case 'days':
        case 'months':
        case 'integer': {
            if (typeof input === 'number') {
                return Number.isInteger(input) ? input.toString() : undefined;
            }
            if (typeof input === 'string') {
                const trimmed = input.trim();
                if (trimmed === '') {
                    return null;
                }
                const num = Number(trimmed);
                return isNaN(num) || !Number.isInteger(num) ? undefined : num.toString();
            }
            return undefined;
        }

        case 'boolean':
            if (typeof input === 'boolean') {
                return input ? 'true' : 'false';
            }
            if (typeof input === 'string') {
                return input.toLowerCase() === 'true' ? 'true' : 'false';
            }
            return undefined;

        case 'putCallRight':
        case 'considerationType':
        case 'considerationTreatment':
            if (typeof input === 'number') {
                return input.toString();
            }
            if (typeof input === 'string') {
                const num = parseInt(input, 10);
                return isNaN(num) ? undefined : num.toString();
            }
            return undefined;

        case 'text':
            if (typeof input === 'string') {
                const trimmed = input.trim();
                return trimmed.length > 0 ? trimmed : null;
            }
            return undefined;

        default:
            return undefined;
    }
};

export const toDisplayValue = (field: ConsiderationField, wire: string | null): string => {
    if (wire == null || wire === '') {
        return '';
    }

    const kind = getFieldKind(field);

    switch (kind) {
        case 'currency': {
            const num = parseFloat(wire);
            return isNaN(num) ? '' : formatMoney(num);
        }

        case 'percent': {
            const num = parseFloat(wire);
            return isNaN(num) ? '' : formatPercent(num);
        }

        case 'multiple': {
            const num = parseFloat(wire);
            return isNaN(num) ? '' : formatMultiple(num);
        }

        case 'ratio':
            return `${wire} : 1`;

        case 'months': {
            const num = parseInt(wire, 10);
            return isNaN(num) ? '' : `${num} months`;
        }

        case 'days':
        case 'integer': {
            const num = parseInt(wire, 10);
            return isNaN(num) ? '' : num.toString();
        }

        case 'boolean':
            return wire === 'true' ? 'Yes' : 'No';

        case 'putCallRight': {
            const ordinal = parseInt(wire, 10);
            return isNaN(ordinal) ? '' : (PUT_CALL_LABELS[ordinal as PutCallRight] ?? '');
        }

        case 'considerationType': {
            const ordinal = parseInt(wire, 10);
            return isNaN(ordinal) ? '' : considerationTypeName(ordinal as TypeOfConsideration);
        }

        case 'considerationTreatment': {
            const ordinal = parseInt(wire, 10);
            return isNaN(ordinal)
                ? ''
                : considerationTreatmentName(ordinal as ConsiderationTreatment);
        }

        case 'text':
            return wire;

        default:
            return wire;
    }
};

const ZERO_WIRE_VALUE = '0';

const NUMERIC_FIELD_KINDS: ConsiderationFieldKind[] = [
    'currency',
    'percent',
    'multiple',
    'ratio',
    'days',
    'months',
    'integer',
];

export const toOriginalDisplayValue = (field: ConsiderationField, wire: string | null): string => {
    if (wire != null && wire !== '') {
        return toDisplayValue(field, wire);
    }
    const kind = getFieldKind(field);
    return NUMERIC_FIELD_KINDS.includes(kind) ? toDisplayValue(field, ZERO_WIRE_VALUE) : 'NA';
};

export const partyLabel = (party: TimelineParty): string => {
    switch (party) {
        case TimelineParty.Buyer:
            return 'Buyer';
        case TimelineParty.Seller:
            return 'Seller';
        case TimelineParty.Broker:
            return 'Advisor';
        case TimelineParty.VetValue:
            return 'VetValue';
        default:
            return '';
    }
};
